/**
 * Data Source Adapter Utilities - Thin Delegation Layer
 * 
 * This module provides backwards-compatible functions that delegate to the new
 * interface-based adapter system. All complex logic has been moved to concrete
 * adapters (FulcrumAdapter, DjangoAdapter) while maintaining full backwards compatibility.
 * 
 * ARCHITECTURE: Components → This File → AdapterFactory → Concrete Adapters
 */

import { 
  FieldMapping, 
  StandardizedDataItem, 
  DataSourceAdapter,
  RelatedEntityItem,
  ApiEndpoint,
  FlexibleDataItem,
  EntityItem,
  GeoItem,
  MediaItemDetailed
} from '../types';

// Import the new adapter system
import { adapterFactory, createAdapterSystem } from './adapters';
import { extractFulcrumField } from './adapters/SharedAdapterUtils';

// ============================================================================
// Adapter System Initialization
// ============================================================================

// Initialize the adapter system on first import
let isInitialized = false;

const initializeAdapterSystem = () => {
  if (!isInitialized) {
    console.log('[AdapterSystem] NEW VERSION - Initialized successfully...');
    createAdapterSystem();
    isInitialized = true;
  }
};

// Initialize immediately
initializeAdapterSystem();

// ============================================================================
// Utility Functions (Preserved from original)
// ============================================================================

/**
 * Extract field value using dot notation path
 */
export const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Get the data source type for an item
 */
export const getDataSourceType = (item: any, endpoint?: any): string => {
  // PRIORITY 1: Use endpoint configuration (source of truth)
  if (endpoint?.dataSourceType) {
    return endpoint.dataSourceType;
  }
  
  // PRIORITY 2: Check for explicit data source markers
  if (item?._source?.type) {
    return item._source.type;
  }
  
  // PRIORITY 3: Infer from data structure (fallback)
  if (item?.fulcrum_data?.form_id || item?.fulcrum_photo?.photo_id) {
    return 'fulcrum';
  }
  
  // PRIORITY 4: Default to Django
  return 'django';
};

/**
 * Helper to detect if a URL is an image
 */
export const isImageUrl = (url: string | null): boolean => {
  if (!url) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
  const lowerUrl = url.toLowerCase();
  return imageExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('image');
};

/**
 * Extract field from data using field mapping
 */
export const extractField = (
  data: any,
  fieldPath: string | string[] | undefined,
  defaultValue: any = null
): any => {
  if (!data || !fieldPath) return defaultValue;
  
  const paths = Array.isArray(fieldPath) ? fieldPath : [fieldPath];
  
  for (const path of paths) {
    const value = getNestedValue(data, path);
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  
  return defaultValue;
};

/**
 * Apply transformer function to field value
 */
export const applyTransformer = (
  fieldName: string,
  value: any,
  originalData: any,
  transformers?: Record<string, (value: any, originalData: any) => any>
): any => {
  if (transformers && transformers[fieldName]) {
    return transformers[fieldName](value, originalData);
  }
  return value;
};

/**
 * Standardize ID to number format
 */
export const standardizeId = (id: any): number => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// ============================================================================
// Safe Adapter Access (with error handling)
// ============================================================================

const getAdapterSafely = (dataSourceType: string) => {
  try {
    initializeAdapterSystem();
    return adapterFactory.getAdapter(dataSourceType);
  } catch (error) {
    console.warn(`[AdapterSystem] Failed to get adapter for ${dataSourceType}, falling back to django:`, error);
    return adapterFactory.getAdapter('django');
  }
};

// ============================================================================
// Backwards-Compatible Delegation Functions
// ============================================================================

/**
 * Fetch data from an endpoint - delegates to appropriate adapter
 */
export const fetchEndpointData = async (
  endpoint: ApiEndpoint,
  userProfile: any,
  options: {
    isLoadMore?: boolean;
    paginationUrl?: string;
    filters?: Record<string, string>;
    fields?: string;
  } = {}
): Promise<{
  data: any[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  } | null;
}> => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  return adapter.fetchEndpointData(endpoint, userProfile, options);
};

/**
 * Search an endpoint - delegates to appropriate adapter
 */
export const searchEndpoint = async (
  endpoint: ApiEndpoint,
  searchTerm: string,
  userProfile: any,
  options: {
    filters?: Record<string, string>;
    fields?: string;
  } = {}
): Promise<{
  data: any[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  } | null;
}> => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  return adapter.searchEndpoint(endpoint, searchTerm, userProfile, options);
};

/**
 * Get item name - delegates to appropriate adapter
 */
export const getDataItemName = (item: any, endpoint?: ApiEndpoint): string => {
  if (!item) return 'Unknown Item';
  
  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    const result = adapter.getDataItemName(item, endpoint);
    return result || 'Unknown Item';
  } catch (error) {
    console.warn(`[getDataItemName] Adapter error for ${dataSourceType}:`, error);
    return item.name || item.title || item.label || 'Unknown Item';
  }
};

/**
 * Get item description - delegates to appropriate adapter
 */
export const getDataItemDescription = (item: any, endpoint?: ApiEndpoint): string => {
  if (!item) return '';
  
  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    const result = adapter.getDataItemDescription(item, endpoint);
    return result || '';
  } catch (error) {
    console.warn(`[getDataItemDescription] Adapter error for ${dataSourceType}:`, error);
    return item.description || item.notes || item.comment || '';
  }
};

/**
 * Get item thumbnail - delegates to appropriate adapter
 */
export const getItemThumbnail = (item: any, endpoint?: any): string | null => {
  if (!item) return null;
  
  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.getItemThumbnail(item, endpoint);
  } catch (error) {
    console.warn(`[getItemThumbnail] Adapter error for ${dataSourceType}:`, error);
    return item.thumbnail || null;
  }
};

/**
 * Get primary image - delegates to appropriate adapter
 */
export const getDataItemPrimaryImage = (item: any): string | null => {
  if (!item) return null;
  
  const dataSourceType = getDataSourceType(item);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.getDataItemPrimaryImage(item);
  } catch (error) {
    console.warn(`[getDataItemPrimaryImage] Adapter error for ${dataSourceType}:`, error);
    return item.primary_image?.url || item.thumbnail || null;
  }
};

/**
 * Get related media - delegates to appropriate adapter
 */
export const getRelatedMedia = (item: any): MediaItemDetailed[] => {
  if (!item) return [];
  
  const dataSourceType = getDataSourceType(item);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.getRelatedMedia(item);
  } catch (error) {
    console.warn(`[getRelatedMedia] Adapter error for ${dataSourceType}:`, error);
    return [];
  }
};

/**
 * Fetch item with relationships - delegates to appropriate adapter
 */
// LEVEL 1: Fetch minimal item data (name, description, thumbnail only)
export const fetchItemSummary = async (
  itemId: string | number, 
  endpoint: ApiEndpoint, 
  userProfile: any, 
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  console.log(`🔵 [LEVEL 1] fetchItemSummary called:`, {
    itemId,
    dataSourceType,
    endpointName: endpoint.name,
    adapterType: adapter.constructor.name
  });
  
  try {
    const result = await adapter.fetchItemSummary(itemId, endpoint, userProfile, onProgress);
    console.log(`✅ [LEVEL 1] fetchItemSummary completed:`, {
      itemId,
      resultName: result?.name,
      hasCompleteData: result?.isComplete
    });
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 1] fetchItemSummary error for ${dataSourceType}:`, error);
    throw error;
  }
};

// LEVEL 2: Fetch complete item data (all properties but no relationships)
export const fetchItem = async (
  itemId: string | number, 
  endpoint: ApiEndpoint, 
  userProfile: any, 
  allApiEndpoints: ApiEndpoint[] = [],
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  console.log(`🟡 [LEVEL 2] fetchItem called:`, {
    itemId,
    dataSourceType,
    endpointName: endpoint.name,
    adapterType: adapter.constructor.name,
    includeRelationships: false
  });
  
  try {
    const result = await adapter.fetchItem(itemId, endpoint, userProfile, allApiEndpoints, onProgress);
    console.log(`✅ [LEVEL 2] fetchItem completed:`, {
      itemId,
      resultName: result?.name,
      hasCompleteData: result?.isComplete,
      hasRelationships: !!result?.relationships
    });
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 2] fetchItem error for ${dataSourceType}:`, error);
    
    // For Fulcrum "not found" errors, provide more context
    if (dataSourceType === 'fulcrum' && error.message?.includes('not found')) {
      console.warn(`[fetchItem] Fulcrum record ${itemId} not found - may be deleted or moved`);
      throw new Error(`Fulcrum record ${itemId} not found - it may have been deleted or moved`);
    }
    
    throw error;
  }
};

// LEVEL 3: Fetch complete item data with all relationships
export const fetchItemWithRelationships = async (
  itemId: string | number, 
  endpoint: ApiEndpoint, 
  userProfile: any, 
  allApiEndpoints: ApiEndpoint[] = [],
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  console.log(`🔴 [LEVEL 3] fetchItemWithRelationships called:`, {
    itemId,
    dataSourceType,
    endpointName: endpoint.name,
    adapterType: adapter.constructor.name,
    includeRelationships: true
  });
  
  try {
    const result = await adapter.fetchItemWithRelationships(itemId, endpoint, userProfile, allApiEndpoints, onProgress);
    console.log(`✅ [LEVEL 3] fetchItemWithRelationships completed:`, {
      itemId,
      resultName: result?.name,
      hasCompleteData: result?.isComplete,
      hasRelationships: !!result?.relationships,
      mediaCount: result?.relationships?.media?.length || 0,
      relatedEntitiesCount: result?.relationships?.related_entities ? Object.keys(result.relationships.related_entities).length : 0
    });
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 3] fetchItemWithRelationships error for ${dataSourceType}:`, error);
    
    // For Fulcrum "not found" errors, provide more context
    if (dataSourceType === 'fulcrum' && error.message?.includes('not found')) {
      console.warn(`[fetchItemWithRelationships] Fulcrum record ${itemId} not found - may be deleted or moved`);
      throw new Error(`Fulcrum record ${itemId} not found - it may have been deleted or moved`);
    }
    
    throw error;
  }
};

// LEGACY: Backward compatibility function that routes to appropriate level
export const fetchItemWithRelationshipsLegacy = async (
  itemId: string | number, 
  endpoint: ApiEndpoint, 
  userProfile: any, 
  allApiEndpoints: ApiEndpoint[] = [],
  onProgress?: (step: string, current: number, total: number) => void,
  includeRelationships: boolean = true
): Promise<any> => {
  // Route to appropriate level based on includeRelationships flag
  if (includeRelationships) {
    return fetchItemWithRelationships(itemId, endpoint, userProfile, allApiEndpoints, onProgress);
  } else {
    return fetchItem(itemId, endpoint, userProfile, allApiEndpoints, onProgress);
  }
};

/**
 * Adapt map feature - delegates to appropriate adapter
 */
export const adaptMapFeature = (feature: any, layerId: string, endpoint?: ApiEndpoint): any => {
  const dataSourceType = endpoint?.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    // Use extractFeatureData which is available in the interface
    const extractedData = adapter.extractFeatureData(feature.properties || {}, layerId, endpoint);
    return {
      id: extractedData.id || feature.properties?.id || feature.id,
      name: extractedData.name || feature.properties?.name || 'Unknown Feature',
      properties: feature.properties,
      geometry: feature.geometry,
      ...extractedData
    };
  } catch (error) {
    console.warn(`[adaptMapFeature] Adapter error for ${dataSourceType}:`, error);
    return {
      id: feature.properties?.id || feature.id,
      name: feature.properties?.name || 'Unknown Feature',
      properties: feature.properties,
      geometry: feature.geometry
    };
  }
};

/**
 * Extract feature data - delegates to appropriate adapter
 */
export const extractFeatureData = (
  properties: any,
  layerId: string,
  endpoint?: ApiEndpoint
): any => {
  const dataSourceType = endpoint?.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.extractFeatureData(properties, layerId, endpoint);
  } catch (error) {
    console.warn(`[extractFeatureData] Adapter error for ${dataSourceType}:`, error);
    return {
      id: properties.id,
      name: properties.name,
      description: properties.description
    };
  }
};

// ============================================================================
// Legacy Compatibility Functions
// ============================================================================

/**
 * @deprecated Use getRelatedMedia instead
 */
export const getRelatedImages = getRelatedMedia;

/**
 * @deprecated Use getDataItemPrimaryImage instead
 */
export const getItemPrimaryImage = getDataItemPrimaryImage;

/**
 * @deprecated Use getRelatedMedia instead
 */
export const getItemPrimaryMedia = (item: any): any | null => {
  const media = getRelatedMedia(item);
  return media.length > 0 ? media[0] : null;
};

// ============================================================================
// Fulcrum-Specific Utilities (Preserved for backwards compatibility)
// ============================================================================

/**
 * Detect Fulcrum region from form ID
 */
export const detectFulcrumRegion = (formId?: string): 'au' | 'us' => {
  if (!formId) return 'au';
  
  // Australian forms typically have longer IDs (36+ chars with dashes)
  // US forms typically have shorter IDs (32 chars or less)
  if (formId.length > 30) {
    return 'au';
  }
  
  return 'us';
};

/**
 * Construct Fulcrum photo URL with region detection
 */
export const constructFulcrumPhotoUrl = (
  photoId: string, 
  formId?: string, 
  thumbnail: boolean = true
): string => {
  if (!photoId) {
    throw new Error(`Invalid photoId: ${photoId}`);
  }
  
  const region = detectFulcrumRegion(formId);
  const baseUrl = region === 'au' ? 'https://api.fulcrumapp-au.com' : 'https://api.fulcrumapp.com';
  const endpoint = thumbnail ? `/api/v2/photos/${photoId}/thumbnail.jpg` : `/api/v2/photos/${photoId}.jpg`;
  return `${baseUrl}${endpoint}`;
};

// ============================================================================
// GeoJSON Adapter (Preserved for Fulcrum data processing)
// ============================================================================

/**
 * Convert GeoJSON features to CultureMap entity items
 */
export const geoJsonToCultureMapAdapter = (
  geoJsonData: any,
  endpoint?: ApiEndpoint
): FlexibleDataItem[] => {
  if (!geoJsonData?.features || !Array.isArray(geoJsonData.features)) {
    console.warn('[GeoJSON-Adapter] Invalid GeoJSON data - no features array');
    return [];
  }

  return geoJsonData.features.map((feature: any, index: number): FlexibleDataItem => {
    const properties = feature.properties || {};
    const geometry = feature.geometry;
    
    const name = properties.site_name ||
                 properties.name ||
                 properties.title ||
                 properties.label ||
                 properties.job_description ||
                 properties.job_id ||
                 `${endpoint?.name || 'Feature'} ${index + 1}`;

    const description = properties.comments ||
                       properties.description ||
                       properties.notes ||
                       properties.comment ||
                       '';

    // Photo detection and URL construction
    let thumbnail = undefined;
    let primaryImage = undefined;
    
    const photoFields = Object.entries(properties).filter(([key, value]) => 
      (key.toLowerCase().includes('photo') && 
       typeof value === 'string' && 
       value.length > 0 && 
       value !== ',' &&
       !key.toLowerCase().includes('caption') &&
       !key.toLowerCase().includes('url')) ||
      (Array.isArray(value) && value.length > 0 && value.some((item: any) => item && typeof item === 'object' && item.photo_id))
    );
    
    if (photoFields.length > 0) {
      const firstPhotoField = photoFields[0];
      const photoData = firstPhotoField[1];
      let photoId = null;
      
      if (Array.isArray(photoData) && photoData.length > 0) {
        const firstPhoto = photoData[0];
        if (firstPhoto?.photo_id) {
          photoId = firstPhoto.photo_id;
        }
      } else if (typeof photoData === 'string' && photoData.length > 0) {
        if (!photoData.includes('http') && !photoData.includes('thumbnail.jpg') && !photoData.includes('/photos/')) {
          const photoIds = photoData.split(',').map(id => id.trim()).filter(id => id.length > 0);
          if (photoIds.length > 0) {
            const candidateId = photoIds[0];
            const invalidValues = ['yes', 'no', 'true', 'false', 'null', 'undefined', 'none', 'n/a'];
            const isValidPhotoId = !candidateId.includes('/') && 
                                  !candidateId.includes('.') && 
                                  candidateId.length >= 8 && 
                                  !invalidValues.includes(candidateId.toLowerCase()) &&
                                  /^[a-f0-9-]{8,}$/i.test(candidateId);
            
            if (isValidPhotoId) {
              photoId = candidateId;
            }
          }
        }
      }
      
      if (photoId) {
        const formId = properties.form_id || endpoint?.formId;
        thumbnail = constructFulcrumPhotoUrl(photoId, formId, true);
        const photoUrl = constructFulcrumPhotoUrl(photoId, formId, false);
        primaryImage = {
          url: photoUrl,
          thumbnail: thumbnail
        };
      }
    }

    const baseItem: EntityItem = {
      id: properties.fulcrum_id || properties.source_id || properties.id || `feature_${index}`,
      name: name,
      description: description,
      thumbnail: thumbnail,
      primary_image: primaryImage,
      created_at: properties.created_at,
      last_updated_at: properties.updated_at || properties.system_updated_at,
      relationships: {
        related_entities: {},
        geo_entities: {},
        media: []
      }
    };

    if (geometry) {
      const geoItem: GeoItem = {
        ...baseItem,
        geometry: typeof geometry === 'object' ? JSON.stringify(geometry) : geometry
      };
      return geoItem;
    }

    return baseItem;
  });
};

// ============================================================================
// Additional Missing Exports (for backwards compatibility)
// ============================================================================

/**
 * Find correct endpoint for an item by attempting to fetch it
 */
export const findCorrectEndpointForItem = async (
  itemId: string | number,
  itemName: string,
  apiEndpoints: ApiEndpoint[],
  currentEndpoint: ApiEndpoint
): Promise<ApiEndpoint> => {
  console.log('[EndpointFinder] Finding correct endpoint for item:', {
    itemId,
    itemName,
    currentEndpoint: currentEndpoint.name,
    availableEndpoints: apiEndpoints.map(ep => ep.name)
  });

  // Skip validation for Fulcrum endpoints - they fetch data directly from Fulcrum API
  if (currentEndpoint.dataSourceType === 'fulcrum') {
    console.log('[EndpointFinder] Fulcrum endpoint detected - skipping backend validation:', currentEndpoint.name);
    return currentEndpoint;
  }

  // For Django endpoints, try to fetch from the current endpoint first
  try {
    const { apiFetch, REACT_APP_API_ROOT } = await import('./api');
    const url = `${REACT_APP_API_ROOT}${currentEndpoint.api_endpoint}${itemId}/`;
    await apiFetch(url);
    console.log('[EndpointFinder] Item found in current endpoint:', currentEndpoint.name);
    return currentEndpoint;
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      console.log('[EndpointFinder] Item not found in current endpoint, trying others...');
    } else {
      console.warn('[EndpointFinder] Non-404 error, returning current endpoint:', error);
      return currentEndpoint;
    }
  }

  // Try other Django endpoints
  const { apiFetch, REACT_APP_API_ROOT } = await import('./api');
  const djangoEndpoints = apiEndpoints.filter(ep => 
    ep.dataSourceType === 'django' && ep.tableName !== currentEndpoint.tableName
  );

  for (const endpoint of djangoEndpoints) {
    try {
      const url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}${itemId}/`;
      await apiFetch(url);
      console.log('[EndpointFinder] ✅ Item found in endpoint:', endpoint.name);
      return endpoint;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        console.log('[EndpointFinder] Item not found in endpoint:', endpoint.name);
      } else {
        console.warn('[EndpointFinder] Error checking endpoint:', endpoint.name, error);
      }
    }
  }

  console.warn('[EndpointFinder] Item not found in any endpoint, returning current as fallback');
  return currentEndpoint;
};

/**
 * Check if endpoint is editable
 */
export const isEndpointEditable = (endpoint: { 
  dataSourceType?: string; 
  isEditable?: boolean; 
}): boolean => {
  const dataSourceType = endpoint.dataSourceType || 'django';
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.isEndpointEditable(endpoint);
  } catch (error) {
    console.warn(`[isEndpointEditable] Adapter error for ${dataSourceType}:`, error);
    return false; // Safe fallback - not editable if adapter fails
  }
};

/**
 * Open item for editing using data source-specific editing interface
 * - Fulcrum: Opens item in Fulcrum web interface (external redirect)
 * - Django: Returns false to use internal dialog (current behavior)
 */
export const openItemForEditing = (item: any, endpoint?: ApiEndpoint | null): boolean => {
  if (!item) return false;
  
  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);
  
  try {
    return adapter.openItemForEditing(item, endpoint || undefined);
  } catch (error) {
    console.warn(`[openItemForEditing] Adapter error for ${dataSourceType}:`, error);
    return false; // Fallback to internal dialog
  }
};

/**
 * Enhance map click data with data source-specific processing
 */
export const enhanceMapClickData = async (
  feature: any,
  layerId: string,
  endpoint?: ApiEndpoint,
  config?: any
): Promise<any> => {
  const properties = feature.properties || {};
  
  // Extract basic data using the feature data extractor
  const extractedData = extractFeatureData(properties, layerId, endpoint);
  
  // Determine entity type and ID
  const entityType = endpoint?.tableName || layerId;
  // Trust the adapter's ID extraction - don't fall back to name which causes the 404 errors
  const entityId = String(extractedData.id || 'unknown');
  const entityName = extractedData.name || `${layerId} Feature`;
  
  console.log('[enhanceMapClickData] Feature data extraction:', {
    extractedId: extractedData.id,
    extractedName: extractedData.name,
    extractionMethod: extractedData.extractionMethod,
    dataSourceType: endpoint?.dataSourceType,
    finalEntityId: entityId
  });
  
  // Handle geometry and coordinates
  let coordinates: [number, number] | undefined;
  if (feature.geometry?.type === 'Point' && Array.isArray(feature.geometry.coordinates)) {
    coordinates = feature.geometry.coordinates as [number, number];
  }
  
  // Create the standardized geo entity
  const geoEntity = {
    id: entityId,
    name: entityName,
    entityType: entityType,
    layerId: layerId,
    coordinates: coordinates,
    geometry: feature.geometry,
    properties: {
      ...properties,
      ...extractedData,
      fromMapClick: true,
      hasCompleteData: false
    }
  };
  
  console.log('[MapClickEnhancer] Created geo entity:', {
    id: geoEntity.id,
    name: geoEntity.name,
    entityType: geoEntity.entityType,
    dataSourceType: endpoint?.dataSourceType || 'generic'
  });
  
  return geoEntity;
};

/**
 * Adapt data source (legacy function)
 */
export const adaptDataSource = async (
  rawData: any,
  sourceType: string,
  fieldMapping?: FieldMapping,
  apiEndpoints?: ApiEndpoint[]
): Promise<StandardizedDataItem[]> => {
  const adapter = getAdapterSafely(sourceType);
  return adapter.adaptDataSource(rawData, fieldMapping, apiEndpoints);
};

/**
 * Convert to legacy format
 */
export const toLegacyFormat = (item: StandardizedDataItem): any => {
  // Simple conversion - most fields map directly
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    thumbnail: item.thumbnail,
    primary_image: item.primary_image,
    created_at: item.created_at,
    last_updated_at: item.last_updated_at,
    url: item.url,
    media_type: item.media_type,
    geometry: item.geometry,
    coordinates: item.coordinates,
    relationships: item.relationships || {
      related_entities: {},
      geo_entities: {},
      media: []
    }
  };
};

/**
 * Get thumbnail handler (legacy function)
 */
export const getThumbnailHandler = (dataSourceType: string) => {
  return (item: any) => getItemThumbnail(item, { dataSourceType });
};

/**
 * Adapt Fulcrum item (delegates to FulcrumAdapter)
 */
export const adaptFulcrumItem = async (
  rawItem: any,
  fieldMapping?: FieldMapping,
  apiEndpoints: ApiEndpoint[] = [],
  userProfile: any = null,
  onProgress?: (step: string, current: number, total: number) => void,
  includeRelationships: boolean = true
): Promise<StandardizedDataItem> => {
  const adapter = getAdapterSafely('fulcrum');
  
  // Check if the adapter has the adaptFulcrumItem method
  if ('adaptFulcrumItem' in adapter && typeof adapter.adaptFulcrumItem === 'function') {
    return adapter.adaptFulcrumItem(rawItem, fieldMapping, apiEndpoints, userProfile, onProgress, includeRelationships);
  }
  
  // Fallback: use adaptDataSource for single item
  const results = await adapter.adaptDataSource([rawItem], fieldMapping);
  return results[0] || {
    id: rawItem.id || 0,
    name: rawItem.name || 'Unknown Item',
    description: rawItem.description || '',
    thumbnail: null,
    primary_image: null,
    created_at: rawItem.created_at || null,
    last_updated_at: rawItem.last_updated_at || null,
    relationships: {
      related_entities: {},
      geo_entities: {},
      media: []
    }
  };
};

/**
 * Map form fields (delegates to FulcrumAdapter)
 */
export const mapFormFields = (formValues: Record<string, any>, formSchema: any): Record<string, any> => {
  // This is a complex function that should be handled by the FulcrumAdapter
  // For now, return the form values as-is for backwards compatibility
  console.warn('[mapFormFields] This function should be refactored to use FulcrumAdapter');
  return formValues;
};

// ============================================================================
// Original Working Implementation from backup
// ============================================================================

// Helper function to safely extract string value from complex Fulcrum fields
const extractStringValue = (value: any, debugContext: string = ''): string => {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  if (typeof value === 'number') {
    return value.toString();
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    const stringValues = value
      .map(item => extractStringValue(item, debugContext))
      .filter(str => str && str.trim() !== '');
    return stringValues.join(', ');
  }
  
  // Handle objects (like choice fields)
  if (typeof value === 'object') {
    if (value.choice_values && Array.isArray(value.choice_values)) {
      return value.choice_values.join(', ');
    }
    if (value.other_values && Array.isArray(value.other_values)) {
      return value.other_values.join(', ');
    }
    if (value.value !== undefined) {
      return extractStringValue(value.value, debugContext);
    }
    if (value.display !== undefined) {
      return extractStringValue(value.display, debugContext);
    }
    return JSON.stringify(value);
  }
  
  return String(value);
};

/**
 * Unified field extraction with config-first → schema-mapped → heuristic → fallback priority
 */
interface FieldExtractionOptions {
  endpoint?: ApiEndpoint;
  formValues?: Record<string, any>;
  rawItem?: any;
  fieldMapping?: FieldMapping;
  userProfile?: any;
  formSchema?: any;
}

const COMMON_NAME_CANDIDATES = [
  'Site Name', 'site_name', 'name', 'title', 'label', 'Label', 'Name of location ', 'activity_type',
  'native_animal_species', 'weed_species_other', 'species', 'subject', 'common_name', 'scientific_name'
];

const COMMON_DESCRIPTION_CANDIDATES = [
  'description', 'Description', 'notes', 'comment', 'details', 'observations', 'd3f9'
];

const COMMON_FULCRUM_NAME_IDS = ['89e3', '7650', 'fc2e', '1c63'];
const COMMON_FULCRUM_DESC_IDS = ['68af', '631e', 'f5ad'];

// Helper: case-insensitive selection from transformed fields
const getFirstMatchingValueCI = (
  source: Record<string, any> | undefined,
  candidates: string[] | undefined
): string | null => {
  if (!source || !candidates || candidates.length === 0) return null;
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  for (const candidate of candidates) {
    const normalizedCandidate = normalize(candidate);
    for (const [key, value] of Object.entries(source)) {
      if (normalize(key) === normalizedCandidate && value && typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }
  return null;
};

// Pick the best-looking string field from transformed values when explicit mappings fail
const findBestStringField = (formValues: Record<string, any> | undefined): string | null => {
  if (!formValues) return null;
  const entries = Object.entries(formValues).filter(([, v]) => typeof v === 'string' && v);
  if (entries.length === 0) return null;
  const normalize = (s: string) => s.toLowerCase();
  const isUUIDish = (s: string) => /[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s);
  
  // Prioritize meaningful field names over UUIDs
  const meaningful = entries.filter(([k, v]) => !isUUIDish(k) && !isUUIDish(v));
  if (meaningful.length > 0) {
    const [, value] = meaningful[0];
    return value;
  }
  
  const [, value] = entries[0];
  return value;
};

/**
 * Extract display name using unified priority system
 */
export const extractUnifiedDisplayName = (options: FieldExtractionOptions): string => {
  const { endpoint, formValues = {}, rawItem = {}, fieldMapping } = options;
  
  // 1. Config-first: endpoint field mapping
  if (endpoint?.dataSourceConfig?.fieldMapping?.name) {
    const cfg = endpoint.dataSourceConfig.fieldMapping.name as string[];
    const fromCfg = getFirstMatchingValueCI(formValues, Array.isArray(cfg) ? cfg : [cfg]);
    if (fromCfg) return fromCfg;
  }

  // 2. Schema-mapped transformed fields
  const fromTransformed = getFirstMatchingValueCI(formValues, COMMON_NAME_CANDIDATES);
  if (fromTransformed) return fromTransformed;

  // 3. Field mapping fallback
  if (fieldMapping?.name) {
    const mappedName = extractField({ ...rawItem, ...formValues }, fieldMapping.name);
    if (mappedName && mappedName !== 'Unnamed Item') {
      return mappedName;
    }
  }

  // 4. Heuristic best field selection
  const best = findBestStringField(formValues);
  if (best) return best;

  // 5. Common Fulcrum ID patterns
  for (const fieldId of COMMON_FULCRUM_NAME_IDS) {
    const value = extractFulcrumField(formValues, fieldId, fieldId === '1c63' ? 'choice_field' : 'text');
    if (value) return value;
  }

  // 6. Raw item fallbacks
  if (rawItem.name) return rawItem.name;
  if (rawItem.label) return rawItem.label;
  if (rawItem.title) return rawItem.title;

  // 7. Final fallback
  return `Record ${rawItem.id?.substring(0, 8) || 'unknown'}`;
};

/**
 * Extract display description using unified priority system
 */
export const extractUnifiedDisplayDescription = (options: FieldExtractionOptions): string => {
  const { endpoint, formValues = {}, rawItem = {}, fieldMapping } = options;
  
  // 1. Config-first: endpoint field mapping
  if (endpoint?.dataSourceConfig?.fieldMapping?.description) {
    const cfg = endpoint.dataSourceConfig.fieldMapping.description as string[];
    const fromCfg = getFirstMatchingValueCI(formValues, Array.isArray(cfg) ? cfg : [cfg]);
    if (fromCfg) return fromCfg;
  }

  // 2. Schema-mapped transformed fields
  const fromTransformed = getFirstMatchingValueCI(formValues, COMMON_DESCRIPTION_CANDIDATES);
  if (fromTransformed) return fromTransformed;

  // 3. Field mapping fallback
  if (fieldMapping?.description) {
    const mappedDescription = extractField({ ...rawItem, ...formValues }, fieldMapping.description);
    if (mappedDescription) {
      return extractStringValue(mappedDescription, 'field mapping');
    }
  }

  // 4. Common Fulcrum ID patterns
  for (const fieldId of COMMON_FULCRUM_DESC_IDS) {
    const value = extractStringValue(extractFulcrumField(formValues, fieldId), `field ${fieldId}`);
    if (value && value.trim() !== '') return value;
  }

  // 5. Raw item fallbacks
  const rawDescs = [
    extractStringValue(rawItem.description, 'rawItem.description'),
    extractStringValue(rawItem.notes, 'rawItem.notes')
  ].filter(desc => desc && desc.trim() !== '');
  
  if (rawDescs.length > 0) return rawDescs[0] as string;

  return '';
};

// ============================================================================
// Export Summary
// ============================================================================

console.log(`[AdapterSystem] Thin delegation layer loaded with backwards-compatible exports`);
